import DataGrid, { Column, Item, Toolbar, type DataGridRef } from 'devextreme-react/data-grid';
import LoadIndicator from 'devextreme-react/load-indicator';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { OPS_SITE_GRID_PROPS, OpsGridToolbarItems, OpsSiteGridChrome } from '@jarvis/platform-ui';
import { fetchFileReviewMentionsOverview, type FileReviewMentionsOverview } from '../../../api/fileReviewMentions';

const calloutStyle: CSSProperties = {
  padding: '0.65rem 0.85rem',
  fontSize: 13,
  lineHeight: 1.45,
  color: 'var(--text-primary, #1f2937)',
  background: 'color-mix(in srgb, var(--accent, #2563eb) 8%, var(--surface-raised, #f3f4f6))',
  border: '1px solid color-mix(in srgb, var(--accent, #2563eb) 20%, var(--border-default, #d1d5db))',
  borderRadius: 8
};

const GRID_STATE_KEY = 'webopsui:file-review-mentions-grid:v2';
const FILE_REVIEW_MENTIONS_GRID_COLUMNS = [
  'site',
  'displayName',
  'mentionsEnabled',
  'hubMentionableCount',
  'clientMentionableCount',
  'entraTenantId',
  'fileReviewStorageKey'
] as const;

export function FileReviewMentionsAdminPage() {
  const gridRef = useRef<DataGridRef>(null);
  const [data, setData] = useState<FileReviewMentionsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchFileReviewMentionsOverview());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !data) {
    return (
      <div className="ops-fill-page ops-fill-page--bare" aria-busy="true">
        <LoadIndicator visible height={32} width={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ops-fill-page ops-fill-page--bare">
        <p role="alert" style={{ padding: '1rem' }}>
          {error}
        </p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="ops-fill-page">
      <header className="ops-fill-page__header" style={{ padding: '0.75rem 1rem 0' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>File Review Mentions</h2>
          <p style={{ margin: '0.25rem 0 0', fontSize: 14, color: 'var(--text-muted, #6b7280)' }}>
            Hub allowlist, sync health, and per-mount mention counts.
          </p>
        </div>
      </header>

      <div
        className="ops-fill-page__alerts"
        style={{ padding: '0 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
      >
        <div style={calloutStyle} role="note">
          {data.rulesSummary}
        </div>
        {data.hubAllowlist.length > 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted, #6b7280)' }}>
            Portal hub allowlist: {data.hubAllowlist.length} group
            {data.hubAllowlist.length === 1 ? '' : 's'} configured.
          </p>
        ) : null}
      </div>

      <div className="ops-fill-page__grid-host">
        <DataGrid
          ref={gridRef}
          dataSource={data.mounts}
          keyExpr="mountId"
          height="100%"
          width="100%"
          showBorders
          columnAutoWidth
          columnHidingEnabled
          allowColumnResizing
          allowColumnReordering
          columnResizingMode="widget"
          {...OPS_SITE_GRID_PROPS}
          noDataText="No mounts found."
        >
          <OpsSiteGridChrome
            stateStorageKey={GRID_STATE_KEY}
            stateColumns={FILE_REVIEW_MENTIONS_GRID_COLUMNS}
            searchPlaceholder="Search mounts…"
            pageSize={25}
          />
          <Toolbar>
            <OpsGridToolbarItems
              gridRef={gridRef}
              loading={loading}
              onRefresh={() => void load()}
              refreshHint="Refresh mentions overview"
            />
            <Item name="searchPanel" location="after" />
          </Toolbar>
          <Column dataField="site" caption="Site" width={110} />
          <Column dataField="displayName" caption="Mount" minWidth={140} />
          <Column dataField="mentionsEnabled" caption="Mentions" dataType="boolean" width={90} />
          <Column dataField="hubMentionableCount" caption="Hub" width={70} />
          <Column dataField="clientMentionableCount" caption="Clients" width={90} />
          <Column dataField="entraTenantId" caption="Tenant" minWidth={120} visible={false} />
          <Column dataField="fileReviewStorageKey" caption="Storage key" minWidth={120} />
        </DataGrid>
      </div>
    </div>
  );
}
